import React from 'react';
import {
  getActiveWorkflowByCategory,
  getReactionDisplay,
  mergeReactionsWithWorkflowCategory,
  partitionMessageReactions,
  WORKFLOW_CLOSED_STATUS_ID,
  WORKFLOW_REACTION_CATEGORIES,
} from '../../utils/reactionConfig';
import MessageReactionBadge from './MessageReactionBadge';

const TONE_ICON = {
  red: 'bg-rose-50 dark:bg-rose-950/40',
  rose: 'bg-rose-50 dark:bg-rose-950/40',
  amber: 'bg-amber-50 dark:bg-amber-950/40',
  green: 'bg-emerald-50 dark:bg-emerald-950/40',
  blue: 'bg-sky-50 dark:bg-sky-950/40',
  indigo: 'bg-indigo-50 dark:bg-indigo-950/40',
  violet: 'bg-violet-50 dark:bg-violet-950/40',
  cyan: 'bg-cyan-50 dark:bg-cyan-950/40',
  teal: 'bg-teal-50 dark:bg-teal-950/40',
  slate: 'bg-slate-50 dark:bg-slate-800/60',
};

const TONE_LABEL = {
  red: 'text-rose-500',
  rose: 'text-rose-500',
  amber: 'text-amber-500',
  green: 'text-emerald-600',
  blue: 'text-sky-600',
  indigo: 'text-indigo-600',
  violet: 'text-violet-600',
  cyan: 'text-cyan-600',
  teal: 'text-teal-600',
  slate: 'text-slate-500',
};

const categoryWord = (categoryId) => {
  const last = String(categoryId || '').split('-').filter(Boolean).pop() || '';
  if (!last) return '';
  return last.charAt(0).toUpperCase() + last.slice(1);
};

const valueWord = (label) => {
  const parts = String(label || '').trim().split(/\s+/).filter(Boolean);
  return parts[parts.length - 1] || String(label || '');
};

const CATEGORY_ORDER = WORKFLOW_REACTION_CATEGORIES.map((category) => category.id);

const TONE_STATUS = {
  red: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-200',
  rose: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-200',
  amber: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-100',
  green: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200',
  blue: 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-200',
  indigo: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200',
  violet: 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-200',
  cyan: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-200',
  teal: 'bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-200',
  slate: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200',
};

const decodeEntities = (value) => {
  if (!value || typeof document === 'undefined') {
    return String(value || '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"');
  }
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const stripPlain = (html) => {
  if (!html) return '';
  let text = decodeEntities(String(html));
  if (/<[a-z][\s\S]*>/i.test(text)) {
    if (typeof document !== 'undefined') {
      const tmp = document.createElement('div');
      tmp.innerHTML = text
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n');
      text = tmp.textContent || tmp.innerText || '';
    } else {
      text = text.replace(/<[^>]+>/g, ' ');
    }
  }
  return text
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export const workflowCardTitle = (msg) => {
  const saved = stripPlain(msg?.workflowTitle);
  if (saved) return saved;
  const firstLine = stripPlain(msg?.content).split('\n').map((line) => line.trim()).find(Boolean);
  return firstLine || 'Workflow';
};

export const workflowCardDescription = (msg) => {
  const saved = stripPlain(msg?.workflowDescription);
  const plain = stripPlain(msg?.content);
  const title = workflowCardTitle(msg);
  const withoutLeadTitle = (text) => {
    if (!text || !title) return text || '';
    const lines = text.split('\n');
    const first = (lines[0] || '').trim();
    if (first && first.toLowerCase() === title.trim().toLowerCase()) {
      return lines.slice(1).join('\n').replace(/^\s+/, '');
    }
    return text;
  };
  if (saved && saved !== title) return withoutLeadTitle(saved);
  if (plain && plain !== title) return withoutLeadTitle(plain);
  return '';
};

const ChevronRightIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
  </svg>
);

const DocIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const WorkflowTimelineCard = ({
  msg,
  linkedTask = null,
  variant = 'workflow',
  currentUserId,
  timeLabel,
  assigneeSlot,
  extra,
  onReactionClick,
  onOpen,
  tagInputOpen = false,
  tagInputDraft = '',
  tagInputRef = null,
  onOpenTagInput,
  onTagDraftChange,
  onSubmitTag,
  onCloseTagInput,
  onRemoveTag,
}) => {
  const isTask = variant === 'task';
  const title = isTask
    ? (stripPlain(linkedTask?.title) || stripPlain(msg?.content).split('\n').map((line) => line.trim()).find(Boolean) || 'Task')
    : workflowCardTitle(msg);
  const description = (() => {
    if (isTask) {
      const fromTask = stripPlain(linkedTask?.description);
      const plain = stripPlain(msg?.content);
      if (fromTask && fromTask !== title) return fromTask;
      if (plain && plain !== title) return plain;
      return '';
    }
    return workflowCardDescription(msg);
  })();
  const reactions = mergeReactionsWithWorkflowCategory(msg.reactions || [], msg.workflowByCategory);
  const { free } = partitionMessageReactions(reactions);
  const byCategory = getActiveWorkflowByCategory(reactions);
  const typeId = byCategory['work-item-type'];
  const statusId = byCategory['workflow-status'];
  const categoryBlocks = Object.entries(byCategory)
    .filter(([category]) => category && category !== 'work-item-type')
    .map(([category, reactionId]) => {
      const display = getReactionDisplay(reactionId) || {};
      return {
        category,
        reactionId,
        label: categoryWord(category),
        value: valueWord(display.label || reactionId.split(':').pop() || ''),
        emoji: display.emoji || '•',
        tone: display.tone || 'slate',
      };
    })
    .sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a.category);
      const bi = CATEGORY_ORDER.indexOf(b.category);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  const customTags = Array.isArray(msg.customTags) ? msg.customTags : [];
  const canAddTags = Boolean(onOpenTagInput && (msg.id || msg._id) && msg.status !== 'failed' && msg.status !== 'pending');
  const hasAssignedTags = free.length > 0 || customTags.length > 0;
  const addTagControl = canAddTags ? (tagInputOpen ? (
    <input
      ref={tagInputRef}
      type="text"
      value={tagInputDraft}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => onTagDraftChange?.(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onSubmitTag?.();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          onCloseTagInput?.();
        }
      }}
      onBlur={() => {
        if (String(tagInputDraft || '').trim()) onSubmitTag?.();
        else onCloseTagInput?.();
      }}
      placeholder="Add tag..."
      maxLength={64}
      className="h-6 w-28 px-2 text-xs rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-primary-500/60 flex-shrink-0"
    />
  ) : (
    <button
      type="button"
      onMouseDown={(event) => event.preventDefault()}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpenTagInput?.();
      }}
      className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-dashed border-slate-400/80 dark:border-slate-500 text-slate-500 dark:text-slate-400 text-xs font-semibold leading-none hover:bg-white hover:text-slate-700 dark:hover:bg-slate-800 flex-shrink-0"
      title="Add tag"
      aria-label="Add tag"
    >
      +
    </button>
  )) : null;
  const typeDisplay = typeId
    ? getReactionDisplay(typeId)
    : (isTask
      ? { emoji: '📋', label: 'Task', tone: 'amber' }
      : { emoji: '🗂️', label: 'Workflow', tone: 'indigo' });
  const statusDisplay = statusId ? getReactionDisplay(statusId) : null;
  const isClosed = statusId === WORKFLOW_CLOSED_STATUS_ID;
  const trackId = String(msg.workflowTrackId || '').trim();
  const trackLink = String(msg.workflowTrackLink || '').trim();
  const typeTone = typeDisplay?.tone || (isTask ? 'amber' : 'slate');
  const statusTone = statusDisplay?.tone || (isClosed ? 'green' : 'slate');
  const openLabel = isTask ? 'Open task' : 'Open workflow';

  return (
    <div className="w-full px-3.5 pt-3 pb-2.5 box-border">
      <div className="flex items-start gap-2.5">
        <span className={`flex-shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-xl text-xl ${TONE_ICON[typeTone] || TONE_ICON.slate}`}>
          {typeDisplay?.emoji || '🗂️'}
        </span>
        <div className="min-w-0 flex-1 pt-0.5">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`text-sm font-extrabold uppercase tracking-wide ${TONE_LABEL[typeTone] || TONE_LABEL.slate}`}>
              {typeDisplay?.label || 'Workflow'}
            </span>
            {trackId ? (
              <>
                <span className="text-slate-400/80">|</span>
                {trackLink ? (
                  <a
                    href={trackLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="text-sm font-semibold text-sky-700 dark:text-sky-300 underline underline-offset-2 truncate"
                    title={trackLink}
                  >
                    #{trackId.replace(/^#/, '')}
                  </a>
                ) : (
                  <span className="text-sm font-semibold text-sky-700 dark:text-sky-300 truncate">
                    #{trackId.replace(/^#/, '')}
                  </span>
                )}
              </>
            ) : null}
            {statusDisplay ? (
              <span className={`ml-auto flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${TONE_STATUS[statusTone] || TONE_STATUS.slate}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                {statusDisplay.label}
              </span>
            ) : null}
          </div>
          <h3 className="mt-1 text-[17px] font-bold leading-snug text-slate-900 dark:text-white break-words">
            {title}
          </h3>
        </div>
      </div>

      {description ? (
        <div className="mt-3 rounded-xl bg-white/55 dark:bg-black/20 px-3 py-2.5">
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400 dark:text-slate-500 mb-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 4h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0119 10.414V19a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
            </svg>
            Description
          </div>
          <p className="text-[13px] leading-relaxed text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">
            {description}
          </p>
        </div>
      ) : null}

      {extra}

      {categoryBlocks.length > 0 ? (
        <div className="mt-3 grid grid-cols-4 rounded-xl bg-white/85 dark:bg-white/10 overflow-hidden">
          {categoryBlocks.map((block) => (
            <button
              key={block.category}
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onReactionClick?.(block.reactionId);
              }}
              className="min-w-0 flex flex-col items-start gap-1 px-1.5 py-2 text-left border-r border-b border-slate-200/70 dark:border-white/10 [&:nth-child(4n)]:border-r-0 hover:bg-white dark:hover:bg-white/10"
              title={`${block.label}: ${block.value}`}
            >
              <span className="flex items-center gap-1 min-w-0 w-full">
                <span className={`flex-shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-md text-[11px] ${TONE_ICON[block.tone] || TONE_ICON.slate}`}>
                  {block.emoji}
                </span>
                <span className="min-w-0 truncate text-[10px] font-medium text-slate-400 dark:text-slate-500 leading-none">
                  {block.label}
                </span>
              </span>
              <span className={`inline-flex max-w-full truncate rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${TONE_STATUS[block.tone] || TONE_STATUS.slate}`}>
                {block.value}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {(hasAssignedTags || addTagControl) ? (
      <div className="mt-2 flex flex-wrap gap-1.5 items-center">
        {free.map((reaction, index) => {
          const isActive = currentUserId != null
            && Array.isArray(reaction.userIds)
            && reaction.userIds.some((id) => String(id) === String(currentUserId));
          return (
            <MessageReactionBadge
              key={`free-${reaction.emoji}-${index}`}
              reactionId={reaction.emoji}
              count={reaction.userIds?.length || 0}
              isOwn={false}
              isActive={isActive}
              onClick={() => onReactionClick?.(reaction.emoji)}
            />
          );
        })}
        {customTags.map((tag) => (
          <span
            key={`tag-${tag.id || tag.label}`}
            className="group/tag inline-flex items-center gap-0.5 max-w-[9rem] min-h-6 px-2 py-0.5 rounded-full text-[11px] font-semibold border border-slate-200 dark:border-slate-600 bg-white/85 dark:bg-slate-800/70 text-slate-600 dark:text-slate-200"
            title={tag.label}
          >
            <span className="truncate leading-tight">{tag.label}</span>
            {onRemoveTag && tag.id ? (
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onRemoveTag(tag.id);
                }}
                className="opacity-0 group-hover/tag:opacity-100 flex-shrink-0 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10 transition-opacity"
                title="Remove tag"
                aria-label={`Remove tag ${tag.label}`}
              >
                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            ) : null}
          </span>
        ))}
        {addTagControl}
      </div>
      ) : null}

      <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-700/80 flex items-center gap-2">
        <div className="min-w-0 flex-1">
          {assigneeSlot}
        </div>
        {timeLabel ? (
          <span className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-medium tabular-nums text-slate-400 dark:text-slate-500">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {timeLabel}
          </span>
        ) : null}
        <span className="flex-shrink-0 h-6 w-px bg-slate-200 dark:bg-slate-700" />
        <button
          type="button"
          data-open-work-item
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpen?.();
          }}
          className="flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-500 hover:text-slate-800 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
          title={openLabel}
          aria-label={openLabel}
        >
          <DocIcon />
        </button>
        <button
          type="button"
          data-open-work-item
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpen?.();
          }}
          className="flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-500 hover:text-slate-800 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
          title={openLabel}
          aria-label={openLabel}
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
};

export default WorkflowTimelineCard;
